/**
 * Operational Bundle Viewer — State Management
 *
 * Pure reducer for viewer state transitions.
 * All transitions are deterministic and side-effect-free.
 */

import type { ViewerState } from './operationalBundleViewerTypes.js';
import { INITIAL_VIEWER_STATE } from './operationalBundleViewerTypes.js';
export { INITIAL_VIEWER_STATE };
import type { OperationalBundleV1, BundleVerificationResult } from '../server/export/operationalBundleTypes.js';
import type { OperationalWorkspaceState } from '../modules/governanceEventStream/operationalRendering/types.js';
import type { AttestationVerificationResult } from '../governanceWorkspace/attestation/operationalBundleAttestationTypes.js';
import type { OperationalLineageGraph, LineageVerificationResult } from '../governanceWorkspace/lineage/operationalLineageTypes.js';

export type ViewerAction =
  | { type: 'LOAD_START' }
  | { type: 'LOAD_SUCCESS'; bundle: OperationalBundleV1 }
  | { type: 'LOAD_ERROR'; message: string }
  | { type: 'VERIFY_START' }
  | { type: 'VERIFY_SUCCESS'; result: BundleVerificationResult; workspace: OperationalWorkspaceState; attestation: AttestationVerificationResult | null; lineageGraph: OperationalLineageGraph; lineageVerification: LineageVerificationResult }
  | { type: 'VERIFY_FAILURE'; result: BundleVerificationResult; workspace: OperationalWorkspaceState | null; attestation: AttestationVerificationResult | null; lineageGraph: OperationalLineageGraph; lineageVerification: LineageVerificationResult }
  | { type: 'ATTESTATION_RESULT'; attestation: AttestationVerificationResult }
  | { type: 'RESET' };

export function viewerReducer(state: ViewerState, action: ViewerAction): ViewerState {
  switch (action.type) {
    case 'LOAD_START':
      return { ...INITIAL_VIEWER_STATE, phase: 'loading' };

    case 'LOAD_SUCCESS':
      return {
        ...state,
        phase: 'verifying',
        bundle: action.bundle,
        errorMessage: null,
      };

    case 'LOAD_ERROR':
      return {
        ...INITIAL_VIEWER_STATE,
        phase: 'error',
        errorMessage: action.message,
      };

    case 'VERIFY_START':
      return { ...state, phase: 'verifying' };

    case 'VERIFY_SUCCESS':
      return {
        ...state,
        phase: 'valid',
        verification: action.result,
        workspace: action.workspace,
        attestation: action.attestation,
        lineageGraph: action.lineageGraph,
        lineageVerification: action.lineageVerification,
        errorMessage: null,
      };

    case 'VERIFY_FAILURE':
      return {
        ...state,
        phase: 'invalid',
        verification: action.result,
        workspace: action.workspace,
        attestation: action.attestation,
        lineageGraph: action.lineageGraph,
        lineageVerification: action.lineageVerification,
      };

    case 'ATTESTATION_RESULT':
      return {
        ...state,
        attestation: action.attestation,
      };

    case 'RESET':
      return INITIAL_VIEWER_STATE;

    default:
      return state;
  }
}
